// libraries
import { useEffect, useState } from 'react';

import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Modal, message, Spin, Rate } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { createXlsxMeterValuesReport } from '../../../../hooks/reports';
import {
  deleteIdleTransactionPayment,
  getTransactionData,
  patchManualApprove,
  postManualCardCharge,
  postManualWalletCharge,
  stopCharging,
} from '../../../../hooks/transactions';
import { WalletChargeConfirmationModal } from '../../../molecules/WalletChargeConfirmationModal';
import { getUserDetail } from '../../../../hooks/users';
import {
  buildPotencyChartSeries,
  ChartPoint,
} from '../../../../utils/buildPotencyChartSeries/buildPotencyChartSeries';
import { potencyTooltipFor } from '../../../../utils/buildPotencyChartTooltip/buildPotencyChartTooltip';
import { hasTransactionManualStop } from '../../../../utils/hasTransactionManualStop/hasTransactionManualStop';
import { modalErrorReport } from '../../../../utils/modalErrorReport/modalErrorReport';
import { COLORS } from '../../../../utils/theme/colors';
import {
  buildPotencyYAxisConfiguration,
  mockedPotencyChartConfiguration,
  potencyYAxisConfiguration,
  socYAxisConfiguration,
  POTENCY_SERIES_COLOR,
  POTENCY_SERIES_NAME,
  SOC_SERIES_COLOR,
  SOC_SERIES_NAME,
} from '../../../__mocks__/IndicatorsPageChartsConfiguration';
import ChartBuilder from '../../../molecules/ChartBuilder';
import {
  IdleFeeContainer,
  IdleFeeLabelContainer,
  IdleFeeTitle,
  IdleFeeValue,
} from '../styled';

const { confirm } = Modal;
const antLoadingIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
interface TranactionSelected {
  transactionId: string;
  station: string;
  autonomy: string;
  chargeBoxPk: string;
  connectorPk: string;
  stopReason: string;
  stopEvent: string;
  stopTimestamp?: string;
  feedbackStars: number;
  feedbackDescription: string;
  idleDurationHumanReadable: string;
  idleDuration: number;
  moneyIdlePaymentTransactionAmount: number;
  moneyIdlePaymentTransactionAmountHumanReadable: string;
  durationTime: string;
  userEmail?: string;
  userPhone?: string;
}

interface ITransactionsCardInnerContent {
  className: string;
  index: number;
  data: any[];
}

export const TransactionsCardInnerContent = ({
  className,
  index,
  data,
}: ITransactionsCardInnerContent) => {
  const [transactionSelected, settransactionSelected] = useState<
    TranactionSelected | undefined
  >(undefined);
  const [transactionChartData, setTransactionChartData] = useState<any>({});
  const [potencyChartConfiguration, setPotencyChartConfiguration] =
    useState<any>(mockedPotencyChartConfiguration);
  const [isExcelLoading, setIsExcelLoading] = useState<any>(false);
  const [isPaymentCancelled, setIsPaymentCancelled] = useState(false);
  const [userData, setUserData] = useState<any>({});
  const [walletModalVisible, setWalletModalVisible] = useState(false);
  const routes = useSelector((state: any) => state.user.routes);

  useEffect(() => {
    if (!transactionSelected?.durationTime) {
      return;
    }

    const duration = moment.duration(transactionSelected.durationTime);

    const fiveHours = moment.duration(5, 'hours');

    setDisabledDownloadMeterValues(
      duration.asMilliseconds() > fiveHours.asMilliseconds(),
    );
  }, [transactionSelected]);

  const [disabledDownloadMeterValues, setDisabledDownloadMeterValues] =
    useState(false);

  useEffect(() => {
    settransactionSelected(data[index]);
  }, [index, data]);

  const { series } = potencyChartConfiguration;

  // Cada série carrega os seus próprios pares (x, y), então basta haver ponto
  // na curva de potência para o gráfico valer a pena.
  const hasChartData = series.some(({ data }) => data?.length);

  const handleDownloadExcel = async (e) => {
    e.stopPropagation();

    setIsExcelLoading(true);

    if (transactionSelected?.transactionId) {
      const xlsxMeterValuesReport = await createXlsxMeterValuesReport(
        transactionSelected.transactionId,
      );

      if (xlsxMeterValuesReport.type === 'error') {
        if (xlsxMeterValuesReport.status === 403) {
          modalErrorReport();
        } else {
          message.error(T('Unable to download the .xlsx file.'));
        }
      }
    }

    setIsExcelLoading(false);
  };

  useEffect(() => {
    (async () => {
      if (transactionSelected?.transactionId) {
        const chartData = await getTransactionData(
          transactionSelected?.transactionId,
        );
        setTransactionChartData(chartData);

        if (chartData?.transaction?.userPk) {
          try {
            const userDetailData = await getUserDetail(
              chartData.transaction.userPk,
            );
            setUserData(userDetailData);
          } catch (error) {
            console.error('Erro ao buscar dados do usuário:', error);
          }
        }
      }
    })();
  }, [transactionSelected]);

  useEffect(() => {
    const { potency, soc, hasSoc } = buildPotencyChartSeries(
      transactionChartData?.values,
    );

    if (!potency.length) {
      return;
    }

    // Uma linha precisa de dois pontos: em sessões com uma única medição só o
    // marcador torna o dado visível. O tamanho é por série justamente para que
    // um SoC de poucas medições não polvilhe marcadores na curva de potência.
    const markerSizeFor = (points: ChartPoint[]) => (points.length > 1 ? 0 : 5);

    setPotencyChartConfiguration((prev) => {
      return {
        ...prev,
        options: {
          ...prev.options,
          markers: {
            // Sem SoC preservamos o comportamento anterior, que não desenhava
            // marcadores na curva de potência.
            size: hasSoc ? [markerSizeFor(potency), markerSizeFor(soc)] : 0,
          },
          colors: hasSoc
            ? [POTENCY_SERIES_COLOR, SOC_SERIES_COLOR]
            : [POTENCY_SERIES_COLOR],
          yaxis: hasSoc
            ? [
                buildPotencyYAxisConfiguration(potency.map(({ y }) => y)),
                socYAxisConfiguration,
              ]
            : potencyYAxisConfiguration,
          // Com duas séries de instantes próprios o tooltip padrão lê as duas
          // pelo mesmo índice de ponto e mostra o SoC de outro instante. Sem
          // SoC não há o que casar: uma série só acerta pelo índice, e o
          // tooltip segue o padrão de antes.
          tooltip: potencyTooltipFor(hasSoc),
          chart: {
            ...prev.options.chart,
            toolbar: {
              show: false,
            },
          },
        },
        series: hasSoc
          ? [
              { name: POTENCY_SERIES_NAME, data: potency },
              { name: SOC_SERIES_NAME, data: soc },
            ]
          : [{ name: POTENCY_SERIES_NAME, data: potency }],
      };
    });
  }, [transactionChartData]);

  const onOkDeleteIdleTransaction = async () => {
    try {
      const result = await deleteIdleTransactionPayment(
        transactionSelected?.transactionId,
      );
      if (result.status === 204) {
        setIsPaymentCancelled(true);
        message.success(T('Idle payment canceled successfully!'));
      } else {
        message.error(T('Failed to cancel idle payment.'));
      }
    } catch (error) {
      message.error(T('An error occurred while canceling the payment.'));
    }
  };

  const showConfirm = (title, onOk) => {
    confirm({
      title: <span style={{ whiteSpace: 'pre-line' }}>{title}</span>,
      icon: <ExclamationCircleOutlined />,
      okText: T('Yes'),
      okType: 'danger',
      cancelText: T('Cancel'),
      onOk,
      onCancel() {},
    });
  };

  const canStopCharging = hasTransactionManualStop();

  const onOkStopCharging = async () => {
    try {
      const result = await stopCharging(transactionSelected?.transactionId);
      if (result?.type === 'success') {
        message.success(T('Charging stopped successfully!'));
        window.location.reload();
      } else {
        message.error(T('Failed to stop charging.'));
      }
    } catch (error) {
      message.error(T('Failed to stop charging.'));
    }
  };

  const hasPermission = routes.find(
    ({ path }) => path === '/cancel-idle-payment',
  );

  const hasManualActionsPerm = routes?.some?.(({ path }: any) =>
    path === '/transactions/manual-actions',
  );

  const txn = transactionChartData?.transaction;
  const paymentType = txn?.paymentType;
  const isPagBankCard = paymentType === 'PAGBANK_CARD';
  const isPagBankAny = paymentType === 'PAGBANK_CARD' || paymentType === 'PAGBANK_PIX';
  // H12: inclui B2B pendente (paga mas não conciliada)
  const isPending =
    txn?.income > 0 &&
    txn?.hasPayment &&
    (!txn?.isPaid || (txn?.statusBtoB != null && txn?.statusBtoB !== 'ACCREDITED'));

  const showManualApprove = hasManualActionsPerm && isPending && !isPagBankAny;
  const showWalletCharge = hasManualActionsPerm && isPending;
  const showCardCharge = hasManualActionsPerm && isPending && isPagBankCard;

  const handleManualApprove = () => {
    confirm({
      title: <span style={{ whiteSpace: 'pre-line' }}>{T('Confirm manual approval?')}</span>,
      content: T('Manual approval description'),
      icon: <ExclamationCircleOutlined />,
      okText: T('Yes'),
      okType: 'primary',
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          const result = await patchManualApprove(txn?.id);
          if (result?.type === 'success') {
            message.success(T('Approved manually'));
            // NOTA H8/M25: pequeno delay pra usuário ver o toast antes do reload
            setTimeout(() => window.location.reload(), 800);
          } else {
            const backendMsg = result?.data?.error;
            message.error(backendMsg || T('Manual action failure'));
          }
        } catch (e: any) {
          // Defensivo — createDocumentRequest normalmente não lança, mas rede pode
          message.error(e?.response?.data?.error || T('Manual action failure'));
        }
      },
      onCancel() {},
    });
  };

  const handleCardCharge = () => {
    confirm({
      title: <span style={{ whiteSpace: 'pre-line' }}>{T('Confirm card charge?')}</span>,
      content: T('Card charge description'),
      icon: <ExclamationCircleOutlined />,
      okText: T('Charge credit card'),
      okType: 'danger',
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          const result = await postManualCardCharge(txn?.id);
          if (result?.type === 'success') {
            message.success(T('Card charged'));
            // NOTA H8/M25: pequeno delay pra usuário ver o toast antes do reload
            setTimeout(() => window.location.reload(), 800);
          } else {
            const backendMsg = result?.data?.error;
            message.error(backendMsg || T('Manual action failure'));
          }
        } catch (e: any) {
          // Defensivo — createDocumentRequest normalmente não lança, mas rede pode
          message.error(e?.response?.data?.error || T('Manual action failure'));
        }
      },
      onCancel() {},
    });
  };

  const handleWalletConfirm = async (opts: any) => {
    try {
      const result = await postManualWalletCharge(txn?.id, opts);
      if (result?.type === 'success') {
        const data = result?.data;
        const preAuthFailed = data?.preAuthCancelRequested === true && data?.preAuthCancelled === false;
        if (preAuthFailed) {
          message.warning(T('Wallet charged but pre-auth cancel failed'));
        } else {
          message.success(T('Charge succeeded'));
        }
        setWalletModalVisible(false);
        // NOTA H8/M25: pequeno delay pra usuário ver o toast antes do reload
        setTimeout(() => window.location.reload(), 800);
      } else {
        const backendMsg = result?.data?.error;
        message.error(backendMsg || T('Manual action failure'));
        setWalletModalVisible(false);
      }
    } catch (e: any) {
      // Defensivo — createDocumentRequest normalmente não lança, mas rede pode
      message.error(e?.response?.data?.error || T('Manual action failure'));
      setWalletModalVisible(false);
    }
  };

  const concludedTransaction =
    isPaymentCancelled ||
    !transactionSelected?.moneyIdlePaymentTransactionAmountHumanReadable;

  return (
    <Div className={className}>
      <div className='section'>
        <div className='transaction_details'>
          <div className='station_details'>
            <p className='strong label1'>{T('Autonomy')}</p>
            <p className='strong label2'>{T('Station ID')}</p>
            <p className='strong label3'>{T('Connector ID')}</p>
            <p className='strong label4'>{T('E-mail')}</p>
            <p className='strong label5'>{T('Telefone')}</p>
            <p className='value1'>{transactionSelected?.autonomy} Km</p>
            <p className='value2'>{transactionSelected?.chargeBoxPk}</p>
            <p className='value3'>{transactionSelected?.connectorPk}</p>
            <p className='value4'>
              {userData?.email ||
                userData?.userEmail ||
                transactionChartData?.transaction?.userEmail ||
                '-'}
            </p>
            <p className='value5'>
              {userData?.phone ||
                userData?.cellphone ||
                userData?.userPhone ||
                transactionChartData?.transaction?.userPhone ||
                '-'}
            </p>
          </div>

          {!!transactionSelected?.idleDuration && (
            <IdleFeeContainer>
              <p className='idleTitleInfo'>Ociosidade</p>

              {!!transactionSelected?.idleDurationHumanReadable && (
                <IdleFeeLabelContainer>
                  <IdleFeeTitle>Tempo total</IdleFeeTitle>

                  <IdleFeeValue>
                    <IdleFeeValue>
                      {transactionSelected?.idleDurationHumanReadable}
                    </IdleFeeValue>
                  </IdleFeeValue>
                </IdleFeeLabelContainer>
              )}

              {!!transactionSelected.moneyIdlePaymentTransactionAmountHumanReadable && (
                <IdleFeeLabelContainer>
                  <IdleFeeTitle> Total da cobrança</IdleFeeTitle>

                  <IdleFeeValue>
                    {
                      transactionSelected?.moneyIdlePaymentTransactionAmountHumanReadable
                    }
                  </IdleFeeValue>
                </IdleFeeLabelContainer>
              )}
            </IdleFeeContainer>
          )}

          {hasPermission &&
            !!transactionSelected?.moneyIdlePaymentTransactionAmountHumanReadable && (
              <Button
                className='button_station cursor'
                onClick={() =>
                  showConfirm(
                    T('Confirm cancelation of idle payment?'),
                    onOkDeleteIdleTransaction,
                  )
                }
                disabled={concludedTransaction}
              >
                {concludedTransaction
                  ? T('Idle Payment Canceled')
                  : T('Cancel Idle Payment')}
              </Button>
            )}

          <>
            {showManualApprove && (
              <Button
                className='button_station cursor'
                style={{ marginBottom: 8 }}
                onClick={handleManualApprove}
              >
                {T('Approve manually')}
              </Button>
            )}
            {showWalletCharge && (
              <Button
                className='button_station cursor'
                style={{ marginBottom: 8 }}
                onClick={() => setWalletModalVisible(true)}
              >
                {T('Charge wallet')}
              </Button>
            )}
            {showCardCharge && (
              <Button
                className='button_station cursor'
                style={{ marginBottom: 8 }}
                onClick={handleCardCharge}
              >
                {T('Charge credit card')}
              </Button>
            )}
            <WalletChargeConfirmationModal
              transaction={txn}
              visible={walletModalVisible}
              onCancel={() => setWalletModalVisible(false)}
              onConfirm={handleWalletConfirm}
            />
          </>
          {isExcelLoading ? (
            <div style={{ margin: '8px 6% 0 0' }}>
              <Spin indicator={antLoadingIcon} />
            </div>
          ) : (
            <>
              <div className='button_station_row'>
                <Button
                  disabled={disabledDownloadMeterValues}
                  className='button_station cursor'
                  onClick={handleDownloadExcel}
                >
                  {T('Download Meter Values')}
                </Button>

                {canStopCharging && !transactionSelected?.stopTimestamp && (
                  <Button
                    danger
                    className='button_station button_stop_charging cursor'
                    onClick={() =>
                      showConfirm(
                        T(
                          'Are you sure you want to force-stop the charging?\n\nBefore continuing, try using the "Stop charging" command in Operations. By forcing the stop, the charging will be finalized with the values measured so far.',
                        ),
                        onOkStopCharging,
                      )
                    }
                  >
                    {T('Stop charging')}
                  </Button>
                )}
              </div>

              {disabledDownloadMeterValues && (
                <div className='button_station_message'>
                  Arquivo excede tamanho máximo permitido! Por favor entre em
                  contato com o suporte.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {transactionSelected?.feedbackStars && (
        <div className='customer_review'>
          {T('Avaliação do cliente')}
          <Rate disabled defaultValue={transactionSelected.feedbackStars} />

          <h3 className='userRateDescription'>
            {transactionSelected.feedbackDescription}
          </h3>
        </div>
      )}

      {hasChartData && (
        <ChartBuilder
          options={potencyChartConfiguration.options}
          series={potencyChartConfiguration.series}
          type='line'
          width='600px'
          height='200px'
        />
      )}
    </Div>
  );
};

export default TransactionsCardInnerContent;

const Div = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 20px;
  .card_inner_content {
    background-color: #000;
  }
  .title {
    padding-left: 20px;
  }

  .transaction_details {
    width: 100%;
  }

  .station_details {
    padding-left: 20px;
    width: 40%;
    max-width: 300px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 0 30px 30px;

    p {
      margin: 3px 0;
      font-size: 12px;
      width: max-content;
    }

    .strong {
      font-weight: 600;
      grid-column: 1 / 1;
    }

    .label2 {
      grid-row: 3;
      grid-column: 1;
    }
    .label3 {
      grid-row: 4;
      margin-right: 5px;
      grid-column: 1;
    }
    .label4 {
      grid-row: 5;
      grid-column: 1;
    }
    .label5 {
      grid-row: 6;
      grid-column: 1;
    }

    .value1 {
      grid-row: 2;
      grid-column: 3;
    }
    .value2 {
      grid-row: 3;
      grid-column: 3;
    }
    .value3 {
      grid-row: 4;
      grid-column: 3;
    }
    .value4 {
      grid-row: 5;
      grid-column: 3;
    }
    .value5 {
      grid-row: 6;
      grid-column: 3;
    }
  }

  .customer_review {
    .userRateDescription {
      color: #555;
      font-size: 12px;
      margin-top: 8px;
    }

    display: flex;
    flex-direction: column;
  }

  .section {
    display: flex;
    margin-left: 15px;
  }

  .button_station {
    margin-top: 6px;
    margin-left: 20px;
    margin-right: auto;
    border-radius: 8px;
    width: 180px;
    display: block;
    padding: 10px 0 10px 0;
    text-align: center;
    line-height: 1em;
    text-decoration: none;
    justify-content: space-between;
    font-size: 13px;
    transition: background-color 200ms ease-in-out;
    background-color: rgba(0, 0, 0, 0.05);
    color: #555;

    p {
      margin: 0 0 0 6px;
    }

    :hover path {
      fill: ${COLORS.primary};
    }

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }

  .button_station_row {
    display: flex;
    gap: 12px;
    margin-top: 6px;
    margin-left: 20px;
  }

  .button_station_row .button_station {
    margin-top: 0;
    margin-left: 0;
    margin-right: 0;
  }

  .button_stop_charging {
    background-color: #ff4d4f;
    color: #fff;

    :hover {
      background-color: #d9363e;
      color: #fff;
    }

    :hover path {
      fill: #fff;
    }
  }

  .button_station_message {
    width: 100%;
    margin-top: 6px;
    margin-left: 20px;
    margin-right: auto;
    border-radius: 8px;
    display: block;
    padding: 10px 0 10px 0;
    text-align: center;
    line-height: 1em;
    text-decoration: none;
    justify-content: space-between;
    font-size: 13px;
    transition: background-color 200ms ease-in-out;
    background-color: #fffbe2;
    color: #555;
  }

  .chart_builder_wrapper {
    background-color: #fff;
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    height: max-content;
    padding: 15px 15px 10px;
  }
`;
